import React, { useState } from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

const CHART_PATH = `${TCustomerInsights}.Chart`;

/* The score-panel trend curve, measured on the live canvas at 100%: it runs the panel's full width
   (147 against a 146.82 panel, so it escapes the panel inset) and stands 73 tall, of which the
   line's own rise and fall is 36.4 - the rest is fill below the lowest reading, which is why a
   reading is normalised into the top band rather than over the whole box. */
const TREND_WIDTH = 147;
const TREND_HEIGHT = 73;
const TREND_BAND = 36.4;

/* The outer svg clips at the viewBox edge, so a peak sitting at y=0 loses the upper half of its own
   stroke and the two engines round it differently. One whole pixel of inset covers the half stroke
   and both roundings. */
const TREND_HEADROOM = 1;

export type ScoreTrendHue = 'accent-purple' | 'warning' | 'negative' | 'positive';

/* Each curve is two vertical gradients over its own bounding box: the line runs from a pale stop at
   its highest reading to the metric's hue at its lowest, and the fill from a second, paler stop at
   30% down to transparent at the baseline. The pale stops were solved out of the rendered pixels
   against the #1A2237 panel; the PERX3-REDESIGN pass holds them as the --is-chart-* roles so the
   light variant can restate them. */
const TREND_TINTS: Record<ScoreTrendHue, { line: string; fill: string }> = {
  'accent-purple': {
    line: tokens.color['chart-line-purple'],
    fill: tokens.color['chart-fill-purple']
  },
  warning: { line: tokens.color['chart-line-warning'], fill: tokens.color['chart-fill-warning'] },
  negative: {
    line: tokens.color['chart-line-negative'],
    fill: tokens.color['chart-fill-negative']
  },
  positive: { line: tokens.color['chart-line-positive'], fill: tokens.color['chart-fill-positive'] }
};

/* Readings are placed against their OWN extremes, so a payload on any scale and of any length keeps
   drawing. A flat series has no extremes to divide by and rides the top of the band. */
const trendPointsFor = (readings: number[]): Array<[number, number]> => {
  const high = Math.max(...readings);
  const low = Math.min(...readings);
  const span = high - low;
  const step = readings.length > 1 ? TREND_WIDTH / (readings.length - 1) : 0;

  return readings.map((reading, index) => [
    index * step,
    TREND_HEADROOM + (span === 0 ? 0 : (1 - (reading - low) / span) * TREND_BAND)
  ]);
};

const round = (value: number): string => Number(value.toFixed(2)).toString();

/* Catmull-Rom through every reading, emitted as the cubic Beziers `d` understands: the screen draws
   one smooth curve, not a polyline, and recharts is pinned at 2.5.0 here. */
const trendLinePath = (points: Array<[number, number]>): string =>
  points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${round(point[0])} ${round(point[1])}`;
    }

    const previous = points[index - 1];
    const before = points[Math.max(index - 2, 0)];
    const after = points[Math.min(index + 1, points.length - 1)];

    const c1 = [previous[0] + (point[0] - before[0]) / 6, previous[1] + (point[1] - before[1]) / 6];
    const c2 = [point[0] - (after[0] - previous[0]) / 6, point[1] - (after[1] - previous[1]) / 6];

    return `${path} C ${round(c1[0])} ${round(c1[1])}, ${round(c2[0])} ${round(c2[1])}, ${round(
      point[0]
    )} ${round(point[1])}`;
  }, '');

const trendAreaPath = (line: string): string =>
  `${line} L ${TREND_WIDTH} ${TREND_HEIGHT} L 0 ${TREND_HEIGHT} Z`;

/* Full-bleed by design: the box carries no inset of its own, and a caller that draws it inside a
   padded panel widens it with a negative margin through `className`. `preserveAspectRatio="none"`
   stretches it across a fluid panel while the 73px height stays the measured one. */
const TrendBox = styled.div`
  position: relative;
  width: 100%;
  height: ${TREND_HEIGHT}px;
`;

const ScoreTrend = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
  /* The plot is the affordance, so the cursor says so; nothing here is clickable. */
  cursor: crosshair;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

/* The marker column sits at the hovered reading and is never the pointer target itself - a hit test
   landing on it instead of the plot would fire mouseleave and flicker the whole overlay away. */
const TrendMarker = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  pointer-events: none;
`;

const TrendDropLine = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  border-left: 1px dashed;
  opacity: 0.6;
`;

const TREND_DOT_SIZE = 7;

const TrendDot = styled.span`
  position: absolute;
  left: 0;
  width: ${TREND_DOT_SIZE}px;
  height: ${TREND_DOT_SIZE}px;
  border-radius: 50%;
  margin: -${TREND_DOT_SIZE / 2}px 0 0 -${TREND_DOT_SIZE / 2}px;
  box-shadow: 0 0 0 3px ${tokens.color['surface-card-alt']};
`;

/* Kept in the empty lower half of the plot - the curve only ever occupies the top 36.4 of the 73 -
   so a revealed reading can never cover the figure the panel is headed by. */
const TrendReadout = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 1px 5px;
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.xs};
  background: ${tokens.color['surface-card']};
  color: ${tokens.color['text-title']};
  font-size: ${tokens.type['caption-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['caption-line']};
  white-space: nowrap;
`;

/* The reading is drawn, but a drawn readout is not announced - assistive tech ignores the contents
   of a role=img, and the marker it belongs to is inside one. */
const TrendAnnouncement = styled.span`
  position: absolute;
  overflow: hidden;
  width: 1px;
  height: 1px;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

export interface IScoreTrendChartProps {
  /** Readings in chronological order; fewer than two is a level, not a trajectory, and draws nothing. */
  points: number[];
  /** The deep stop of both gradients, and the marker's own fill. */
  hue: ScoreTrendHue;
  /** Names the plot, and the reading the marker reveals. */
  label: string;
  formatReading: (reading: number) => string;
  /** Disambiguates the gradient ids when several of these are drawn on one card. */
  gradientId: string;
  testId?: string;
  className?: string;
}

/**
 * The gradient-filled area trend drawn under each score panel of `Customer Behaviour Trends`, on
 * Customer Insights (`589-30391`) and on Customer 360 (`3583-10006`) alike.
 *
 * NOT `TrendChart`: this repo is on recharts 2.5.0, whose `Tooltip` is pointer-only, so its hover
 * cannot be reached from a keyboard - which is the one thing a reveal-on-hover reading must be.
 * PLA-8828.
 */
const ScoreTrendChart: React.FC<IScoreTrendChartProps> = ({
  points: readings,
  hue,
  label,
  formatReading,
  gradientId,
  testId = 'score-trend',
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const [active, setActive] = useState<number | null>(null);

  const points = trendPointsFor(readings);
  const line = trendLinePath(points);
  const tint = TREND_TINTS[hue];
  const ink = tokens.color[hue];

  if (readings.length < 2) {
    return null;
  }

  const last = readings.length - 1;
  const marker =
    active === null
      ? null
      : {
          // A share of the RENDERED width, which is what places the overlay over a box the viewBox
          // no longer measures.
          left: (active / last) * 100,
          top: points[active][1],
          reading: formatReading(readings[active]),
          /* Centred on the marker except at the ends, where centring would hang it off a full-bleed
             plot; the first and last fifth anchor it inside instead. */
          shift: active / last < 0.2 ? '0%' : active / last > 0.8 ? '-100%' : '-50%'
        };

  // Nearest reading to the pointer in the box's OWN width, not the viewBox's: the curve is stretched.
  const nearestTo = (clientX: number, box: DOMRect): number =>
    Math.min(last, Math.max(0, Math.round(((clientX - box.left) / box.width) * last)));

  const onKeyDown = (event: React.KeyboardEvent<SVGSVGElement>): void => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      setActive(current => {
        const from = current === null ? last : current;

        return Math.min(last, Math.max(0, from + (event.key === 'ArrowRight' ? 1 : -1)));
      });
    }
  };

  return (
    <TrendBox className={className} data-testid={`${testId}-box`}>
      <ScoreTrend
        viewBox={`0 0 ${TREND_WIDTH} ${TREND_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        tabIndex={0}
        aria-label={t(`${CHART_PATH}.TrendSeries`, { series: label })}
        data-testid={testId}
        data-hue={hue}
        data-points={readings.length}
        onMouseMove={event =>
          setActive(nearestTo(event.clientX, event.currentTarget.getBoundingClientRect()))
        }
        onMouseLeave={() => setActive(null)}
        onFocus={() => setActive(current => (current === null ? last : current))}
        onBlur={() => setActive(null)}
        onKeyDown={onKeyDown}
      >
        <defs>
          {/* Both gradients stay in the default objectBoundingBox, so each spans the box of the path
              that uses it: the fill's is the full 73, the line's only its own 36.4 rise. */}
          <linearGradient id={`score-trend-fill-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tint.fill} stopOpacity="0.3" />
            <stop offset="100%" stopColor={ink} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`score-trend-line-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tint.line} />
            <stop offset="100%" stopColor={ink} />
          </linearGradient>
        </defs>

        <path d={trendAreaPath(line)} fill={`url(#score-trend-fill-${gradientId})`} />
        {/* The stroke is one pixel at every point on the curve and the box is stretched horizontally
            only, so the weight is held off the transform rather than squashed by it. */}
        <path
          d={line}
          fill="none"
          stroke={`url(#score-trend-line-${gradientId})`}
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1="0"
          y1={TREND_HEIGHT - 0.5}
          x2={TREND_WIDTH}
          y2={TREND_HEIGHT - 0.5}
          stroke={tokens.color['text-muted']}
          strokeOpacity="0.1"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      </ScoreTrend>

      {/* The marker is drawn in HTML over the plot rather than inside it: the stretched viewBox would
          leave a circle as an ellipse and the readout's type squashed. */}
      {marker ? (
        <TrendMarker data-testid={`${testId}-marker`} style={{ left: `${marker.left}%` }}>
          <TrendDropLine style={{ top: `${marker.top}px`, borderColor: ink }} />
          <TrendDot style={{ top: `${marker.top}px`, background: ink }} />
          <TrendReadout
            data-testid={`${testId}-readout`}
            style={{ transform: `translateX(${marker.shift})` }}
          >
            {marker.reading}
          </TrendReadout>
        </TrendMarker>
      ) : null}

      <TrendAnnouncement aria-live="polite" data-testid={`${testId}-announcement`}>
        {marker ? t(`${CHART_PATH}.PointReading`, { label, value: marker.reading }) : ''}
      </TrendAnnouncement>
    </TrendBox>
  );
};

export default ScoreTrendChart;
